import { z } from 'zod';

import { startLettersSchema, type PlayerCallModeValue, type RoleStartLetters } from './auction.ts';
import {
  playerRoleSchema,
  type PlayerRoleCounts,
  type PlayerRoleValue,
  type PlayerSummary,
} from './player.ts';

/**
 * Contratto della sequenza di chiamata.
 *
 * La sequenza è **materiale riservato all'amministratore**: conoscere in
 * anticipo quali calciatori arriveranno più avanti cambia la strategia di
 * offerta, quindi queste rotte vivono nello scope amministrativo e nessun
 * payload destinato ai fantallenatori le include. Il partecipante vede il
 * calciatore corrente e la progressione, non ciò che deve ancora arrivare.
 */
export const SEQUENCE_ROUTES = {
  /** Template Fastify; per un URL concreto usa `auctionSequencePath()`. */
  collection: '/auctions/:id/sequence',
} as const;

export function auctionSequencePath(auctionId: string): string {
  return `/auctions/${encodeURIComponent(auctionId)}/sequence`;
}

// ---------------------------------------------------------------------------
// Enum condivisi
// ---------------------------------------------------------------------------

export const AUCTION_PLAYER_STATUSES = ['PENDING', 'CALLING', 'SOLD', 'UNSOLD'] as const;

export type AuctionPlayerStatusValue = (typeof AUCTION_PLAYER_STATUSES)[number];

export const auctionPlayerStatusSchema = z.enum(AUCTION_PLAYER_STATUSES);

export type AuctionPlayerStatusCounts = Readonly<Record<AuctionPlayerStatusValue, number>>;

export const EMPTY_SEQUENCE_PROGRESS: AuctionPlayerStatusCounts = {
  PENDING: 0,
  CALLING: 0,
  SOLD: 0,
  UNSOLD: 0,
};

// ---------------------------------------------------------------------------
// Richiesta
// ---------------------------------------------------------------------------

export const SEQUENCE_SEED_MIN_LENGTH = 8;
export const SEQUENCE_SEED_MAX_LENGTH = 64;

/**
 * Generazione della sequenza.
 *
 * Il seme è opzionale: normalmente lo estrae il server. Poterlo indicare serve a
 * **riprodurre** un ordine — rigenerare identica una sequenza andata persa, o
 * fissarne una in un test — e vale solo per le modalità casuali.
 */
export const generateSequenceRequestSchema = z
  .object({
    /**
     * Da dove parte l'asta: reparto e lettera.
     *
     * Se assenti valgono quelli configurati sull'asta. Indicarli qui è il caso
     * normale: si scelgono generando la sequenza, cioè subito prima di
     * avviare, e la scelta viene salvata nella configurazione.
     */
    startRole: playerRoleSchema.optional(),
    startLetters: startLettersSchema.optional(),
    seed: z
      .string()
      .trim()
      .min(
        SEQUENCE_SEED_MIN_LENGTH,
        `Il seme deve avere almeno ${SEQUENCE_SEED_MIN_LENGTH} caratteri`,
      )
      .max(
        SEQUENCE_SEED_MAX_LENGTH,
        `Il seme non può superare ${SEQUENCE_SEED_MAX_LENGTH} caratteri`,
      )
      .optional(),
  })
  // Il corpo è interamente facoltativo: generare con i valori dell'asta è il
  // caso normale, indicare un seme è l'eccezione.
  .default({});

export type GenerateSequenceRequest = z.input<typeof generateSequenceRequestSchema>;

// ---------------------------------------------------------------------------
// Risposte
// ---------------------------------------------------------------------------

export interface SequenceEntry {
  /** Identificativo dell'`AuctionPlayer`, non del calciatore. */
  readonly id: string;
  /** Posizione 1-based, come la legge l'amministratore. */
  readonly sequenceNumber: number;
  readonly status: AuctionPlayerStatusValue;
  readonly player: PlayerSummary;
}

export interface SequenceResponse {
  /**
   * Le voci **mostrabili**: dove l'ordine è casuale (modalità `RANDOM`, o un
   * reparto su `START_LETTER_RANDOM` in `ROLE_BASED`) i calciatori non ancora
   * chiamati non compaiono — i prossimi restano una sorpresa anche per
   * l'amministratore. `total`, `counts` e `progress` contano invece tutte le
   * righe: la differenza fra `total` e `entries.length` è quanta sequenza è
   * coperta.
   */
  readonly entries: readonly SequenceEntry[];
  readonly total: number;
  /** Modalità con cui la sequenza esistente è stata generata. */
  readonly mode: PlayerCallModeValue;
  /**
   * Seme usato, `null` per la modalità deterministica o se la sequenza non è
   * ancora stata generata.
   */
  readonly seed: string | null;
  /** Reparto da cui parte l'asta; gli altri seguono a rotazione. */
  readonly startRole: PlayerRoleValue;
  /** Lettera da cui è partito l'ordine alfabetico, reparto per reparto. */
  readonly startLetters: RoleStartLetters;
  /** Quanti calciatori per ruolo compongono la sequenza. */
  readonly counts: PlayerRoleCounts;
  /** Avanzamento: quanti chiamati, venduti, invenduti. */
  readonly progress: AuctionPlayerStatusCounts;
}

export interface GenerateSequenceResponse extends SequenceResponse {
  /** Quante posizioni sono state riscritte rispetto alla sequenza precedente. */
  readonly replaced: number;
}

/**
 * Esito dell'eliminazione esplicita (DELETE sulla stessa rotta).
 *
 * Eliminare la sequenza è ciò che riapre la modifica dell'ordine di chiamata
 * in configurazione: finché esiste, modalità e lettere sono congelate perché
 * ne sono gli input esatti.
 */
export interface DiscardSequenceResponse {
  readonly deleted: true;
  /** Quante posizioni sono state rimosse; 0 se non c'era alcuna sequenza. */
  readonly removed: number;
}
